import React, { useEffect, useMemo, useState } from "react";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { postApi } from "../../Services/apiService";
import { toast } from "react-toastify";
import { getStoredUserDetail, hasAuthToken } from "../../Utils/authStorage";
import { syncHeadOfLineRoleFromApi } from "../../Utils/headOfLineRole";
import { ensurePlanLabel } from "../../Utils/subscriptionAccess";
import "../../Auth.css";

const stripePublishableKey = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "").trim();

const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey).catch((err) => {
      console.warn("Stripe failed to load. Payment may be unavailable.", err?.message || err);
      return null;
    })
  : null;

const SUBSCRIPTION_INTENT_PATH =
  process.env.REACT_APP_SUBSCRIPTION_INTENT_PATH || "/subscription/intent";

const LIFETIME_INTENT_PATH =
  process.env.REACT_APP_LIFETIME_INTENT_PATH || "/subscription/lifetime/intent";

const DONATION_INTENT_PATH =
  process.env.REACT_APP_DONATION_INTENT_PATH || "/subscription/donation/intent";

const AMOUNT_IS_CENTS = process.env.REACT_APP_SIGNUP_AMOUNT_IS_CENTS === "true";

function isLifetimePlan(plan, planCategory) {
  if (
    planCategory === "lifetime" ||
    planCategory === "lifetime_promo" ||
    planCategory === "lifetime_mother_promo" ||
    planCategory === "lifetime_father_promo"
  ) {
    return true;
  }
  const pt = plan && Number(plan.plan_type);
  if (pt === 4 || pt === 7) return true;
  const bc = plan?.billing_cycle != null ? String(plan.billing_cycle).trim() : "";
  return bc === "4";
}

function parseLifetimeIntentResponse(res) {
  if (!res || typeof res !== "object") {
    return { clientSecret: "", paymentIntentId: "" };
  }
  const layer =
    res.client_secret != null || res.clientSecret != null ? res : res.data != null ? res.data : res;
  const inner = layer?.data != null && typeof layer.data === "object" ? layer.data : layer;
  const clientSecret = String(inner?.client_secret ?? inner?.clientSecret ?? "").trim();
  const paymentIntentId = inner?.payment_intent_id ?? inner?.paymentIntentId ?? "";
  return { clientSecret, paymentIntentId };
}

const elementOptions = {
  style: {
    base: {
      fontSize: "14px",
      color: "#2c2c2c",
      fontFamily:
        "'Be Vietnam Pro', 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      "::placeholder": {
        color: "#9aa0a6",
        fontFamily:
          "'Be Vietnam Pro', 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      },
    },
    invalid: { color: "#dc3545" },
  },
};

function isSetupIntentClientSecret(secret) {
  return typeof secret === "string" && secret.trimStart().startsWith("seti_");
}

function parseSubscriptionIntentResponse(res) {
  if (!res || typeof res !== "object") {
    return {
      clientSecret: "",
      subscriptionId: "",
      scheduleId: "",
      currentPeriodEnd: null,
      message: "",
      amount: null,
    };
  }
  const hasTopLevel =
    res.clientSecret != null ||
    res.client_secret != null ||
    res.subscriptionId != null ||
    res.schedule_id != null;
  const layer = hasTopLevel ? res : res.data != null && typeof res.data === "object" ? res.data : res;
  const inner = layer?.data != null && typeof layer.data === "object" ? layer.data : layer;

  const clientSecret = String(inner?.clientSecret ?? inner?.client_secret ?? "").trim();
  const subscriptionId = inner?.subscriptionId ?? inner?.subscription_id ?? "";
  const scheduleId = inner?.schedule_id ?? inner?.scheduleId ?? "";
  const currentPeriodEnd = inner?.current_period_end ?? null;
  const message = inner?.message ?? res?.message ?? layer?.message ?? "";
  let amount = inner?.amount ?? layer?.amount ?? null;
  if (AMOUNT_IS_CENTS && typeof amount === "number") {
    amount = amount / 100;
  }
  return { clientSecret, subscriptionId, scheduleId, currentPeriodEnd, message, amount };
}

function formatPlanDisplayNameForBundleCheck(plan) {
  const raw = (plan?.name && String(plan.name).trim()) || "";
  if (!raw) return "";
  const spaced = raw.replace(/_/g, " ");
  const titled = spaced.replace(/\b\w/g, (c) => c.toUpperCase());
  return titled.replace(/(['\u2019])S(?=\s|$)/g, "$1s");
}

/** Same as SubscriptionPlansPicker: combined mother+father lifetime promo card. */
function isMotherFatherSidesPromoPlan(plan) {
  const slug = String(plan?.name ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (
    slug.includes("mother_father_sides") &&
    (slug.includes("promo") || slug.includes("_plan") || slug.endsWith("plan"))
  ) {
    return true;
  }
  const display = formatPlanDisplayNameForBundleCheck(plan).toLowerCase();
  const hasSidesBundle =
    display.includes("mother") &&
    display.includes("father") &&
    display.includes("sides") &&
    (display.includes("promo") || display.includes("plan"));
  return hasSidesBundle;
}

/** Map API `Mother` / `Father` (or "Mother Side", etc.) to payment summary copy. */
function sideLineLabelFromPlanName(raw) {
  const t = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (t === "mother" || t === "mother side" || t === "mothers side") return "Mother's Side";
  if (t === "father" || t === "father side" || t === "fathers side") return "Father's Side";
  return null;
}

const BILLING_CYCLE_LABELS = {
  1: "Monthly",
  2: "3-Month",
  3: "Yearly",
  4: "Lifetime",
};

function billingCycleLabel(plan) {
  const raw = plan?.billing_cycle;
  if (raw === undefined || raw === null || raw === "") return "";
  const key = Number(String(raw).trim());
  if (Number.isFinite(key) && BILLING_CYCLE_LABELS[key]) {
    return BILLING_CYCLE_LABELS[key];
  }
  return "";
}

const LIFETIME_PLAN_CATEGORIES = new Set([
  "lifetime",
  "lifetime_promo",
  "lifetime_mother_promo",
  "lifetime_father_promo",
]);

/** e.g. Father's Side + quarterly → "Father's Side - Quarterly" */
function withBillingCycleSuffix(baseLabel, plan, planCategory) {
  const label = String(baseLabel || "").trim();
  if (!label) return label;

  const cycle = billingCycleLabel(plan);
  if (!cycle) return label;

  const pt = plan?.plan_type != null ? Number(plan.plan_type) : NaN;
  if (LIFETIME_PLAN_CATEGORIES.has(planCategory) || pt === 4 || pt === 7 || cycle === "Lifetime") {
    return label;
  }

  const suffix = ` - ${cycle}`;
  if (label.toLowerCase().endsWith(cycle.toLowerCase()) || label.includes(suffix)) {
    return label;
  }

  return `${label}${suffix}`;
}

/** Payment summary label; API may expose plan name as slug `member_standard`. */
function planCheckoutDisplayName(plan, planCategory) {
  if (planCategory === "member_standard") return "Yearly Household Admin Fee";
  const raw = plan?.name != null ? String(plan.name).trim() : "";
  const normalized = raw.toLowerCase().replace(/\s+/g, "_");
  if (normalized === "member_standard") return "Yearly Household Admin Fee";

  let base;

  if (isMotherFatherSidesPromoPlan(plan)) {
    base = formatPlanDisplayNameForBundleCheck(plan) || raw || "Plan";
  } else {
    const pt = plan?.plan_type != null ? Number(plan.plan_type) : NaN;
    if (Number.isFinite(pt)) {
      if (pt === 1) base = "Mother's Side";
      else if (pt === 2) base = "Father's Side";
      else if (pt === 3) base = "Both Sides of Family";
      else if (pt === 4) base = "Lifetime (Mother's side)";
      else if (pt === 7) base = "Lifetime (Father's side)";
    }

    if (!base) {
      const fromName = sideLineLabelFromPlanName(raw);
      base = fromName || raw || "Subscription";
    }
  }

  return ensurePlanLabel(withBillingCycleSuffix(base, plan, planCategory));
}

function formatPeriodEnd(ts) {
  if (ts == null || !Number.isFinite(Number(ts))) return null;
  const ms = Number(ts) > 1e12 ? Number(ts) : Number(ts) * 1000;
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

function headOfLineTreeIdOverride(familyTreeIdForIntent) {
  if (familyTreeIdForIntent == null) return undefined;
  const t = String(familyTreeIdForIntent).trim();
  return t !== "" ? t : undefined;
}

function SignupScheduledPanel({ scheduleInfo, onContinue, isLoading, familyTreeIdForIntent }) {
  const dateLabel = formatPeriodEnd(scheduleInfo?.currentPeriodEnd);
  return (
    <div className="signup-stripe-form signup-scheduled-panel">
      <div className="signup-stripe-header">
        <h1 className="signup-stripe-title">Plan update scheduled</h1>
        <p className="signup-stripe-subtitle">
          {scheduleInfo?.message ||
            "Your plan change is scheduled. It will take effect at the end of your current billing period."}
        </p>
      </div>
      <div className="signup-payment-summary-card signup-scheduled-summary">
        {dateLabel ? (
          <p className="signup-scheduled-meta">
            <strong>Starts on:</strong> {dateLabel}
          </p>
        ) : null}
        {scheduleInfo?.scheduleId ? (
          <p className="signup-scheduled-meta signup-scheduled-meta--muted">
            Reference: {scheduleInfo.scheduleId}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        className="signup-payment-submit-button"
        onClick={async () => {
          await syncHeadOfLineRoleFromApi(headOfLineTreeIdOverride(familyTreeIdForIntent));
          onContinue?.();
        }}
        disabled={isLoading}
      >
        {isLoading ? "Please wait…" : "Continue to dashboard"}
      </button>
    </div>
  );
}

function SignupPaymentForm({
  amountCents,
  clientSecret,
  onPaymentSuccess,
  planSummaryName,
  showRenewalNotice = false,
  familyTreeIdForIntent,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);

  const amountText = useMemo(() => `$${(amountCents / 100).toFixed(2)}`, [amountCents]);

  const handleCardNumberChange = (event) => {
    if (event.complete && elements) {
      const expiryElement = elements.getElement(CardExpiryElement);
      if (expiryElement) setTimeout(() => expiryElement.focus(), 50);
    }
  };

  const handleExpiryChange = (event) => {
    if (event.complete && elements) {
      const cvcElement = elements.getElement(CardCvcElement);
      if (cvcElement) setTimeout(() => cvcElement.focus(), 50);
    }
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!clientSecret) {
      toast.error("Payment is not ready yet. Please wait a moment and try again.");
      return;
    }
    if (!stripe || !elements) {
      toast.error("Payment form is still loading. Please wait a moment and try again.");
      return;
    }

    setIsLoading(true);
    try {
      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) {
        toast.error("Card element not ready. Please try again.");
        return;
      }

      if (isSetupIntentClientSecret(clientSecret)) {
        const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card: cardNumber },
        });

        if (error) {
          toast.error(error.message || "Payment failed. Please try again.");
          return;
        }

        if (setupIntent?.status === "succeeded") {
          await syncHeadOfLineRoleFromApi(headOfLineTreeIdOverride(familyTreeIdForIntent));
          toast.success("Payment successful");
          await Promise.resolve(onPaymentSuccess?.());
        } else {
          toast.info(`Setup status: ${setupIntent?.status || "unknown"}`);
        }
        return;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardNumber },
      });

      if (error) {
        toast.error(error.message || "Payment failed. Please try again.");
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        await syncHeadOfLineRoleFromApi(headOfLineTreeIdOverride(familyTreeIdForIntent));
        toast.success("Payment successful");
        await Promise.resolve(onPaymentSuccess?.());
      } else {
        toast.info(`Payment status: ${paymentIntent?.status || "unknown"}`);
      }
    } catch (err) {
      toast.error(err?.message || "Payment failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signup-stripe-form">
      <div className="signup-stripe-header">
        <div className="signup-stripe-icon-wrap">
          <svg
            className="signup-stripe-wallet-icon"
            width="72"
            height="72"
            viewBox="0 0 72 72"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <rect width="72" height="72" rx="14" fill="#0165F9" fillOpacity="0.28" />
            <path
              d="M22 26h28a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H22a2 2 0 0 1-2-2V28a2 2 0 0 1 2-2z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M26 26V22a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <rect x="38" y="36" width="8" height="4" rx="1" fill="white" />
          </svg>
        </div>
        <h1 className="signup-stripe-title">Complete Payment</h1>
        <p className="signup-stripe-subtitle">Secure payment for your FamConnect26 account</p>
        <p className="signup-stripe-plan-name">{planSummaryName || "Subscription"}</p>
      </div>

      <div className="signup-payment-summary-card">
        <div className="signup-payment-summary-row signup-payment-summary-total-row">
          <div className="signup-payment-summary-label">Total due now</div>
          <div className="signup-payment-summary-total">{amountText}</div>
        </div>
        <div className="signup-payment-summary-footnote">Secure checkout powered by Stripe.</div>
      </div>

      <form
        onSubmit={handlePay}
        className="signup-payment-card-form"
        noValidate
        autoComplete="off"
        data-form-type="other"
      >
        <div className="signup-payment-field">
          <label className="signup-payment-label">Card Number</label>
          <div
            className="signup-payment-stripe-input signup-payment-stripe-input-with-icon"
            data-autocomplete="off"
          >
            <svg
              className="signup-payment-card-icon"
              width="20"
              height="16"
              viewBox="0 0 24 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <rect x="0.5" y="3.5" width="23" height="12" rx="1.5" stroke="#9aa0a6" strokeWidth="1" />
              <rect x="2" y="6" width="20" height="2" rx="1" fill="#9aa0a6" />
              <rect x="2" y="10" width="8" height="2" rx="1" fill="#9aa0a6" />
            </svg>
            <CardNumberElement
              options={{ ...elementOptions, disableLink: true }}
              onChange={handleCardNumberChange}
            />
          </div>
        </div>

        <div className="signup-payment-row">
          <div className="signup-payment-field">
            <label className="signup-payment-label">Expiry</label>
            <div className="signup-payment-stripe-input" data-autocomplete="off">
              <CardExpiryElement
                options={{ ...elementOptions, disableLink: true }}
                onChange={handleExpiryChange}
              />
            </div>
          </div>
          <div className="signup-payment-field">
            <label className="signup-payment-label">CVC</label>
            <div className="signup-payment-stripe-input" data-autocomplete="off">
              <CardCvcElement options={{ ...elementOptions, disableLink: true }} />
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="signup-payment-submit-button"
          disabled={isLoading || !clientSecret}
        >
          {isLoading ? "Processing..." : `Pay ${amountText}`}
        </button>
        {showRenewalNotice ? (
          <p className="signup-payment-renewal-notice">
            As the Admin, you will be notified one month in advance when your next payment is due.
          </p>
        ) : null}
      </form>
    </div>
  );
}

/**
 * Shared Stripe checkout (intent APIs + Elements). Used by /register/payment and Settings subscription tab.
 */
export default function SubscriptionCheckoutFlow({
  selectedPlan,
  planCategory,
  initialClientSecret = "",
  initialAmount,
  onPaymentSuccess,
  headerSlot = null,
  wrapperClassName = "",
  /** When set (member line checkout), sent as family_tree_id on subscription intent. */
  familyTreeIdForIntent = null,
  /** `'donation'` uses /subscription/donation/intent with donationAmount instead of plan checkout. */
  checkoutType = "plan",
  donationAmount = null,
  /** Overrides payment summary label (e.g. "Donation"). */
  planSummaryNameOverride = "",
}) {
  const [isInitLoading, setIsInitLoading] = useState(true);
  const [initError, setInitError] = useState("");
  const [clientSecret, setClientSecret] = useState(initialClientSecret);
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [feeAmount, setFeeAmount] = useState(() => {
    if (initialAmount != null && Number.isFinite(Number(initialAmount))) return Number(initialAmount);
    if (selectedPlan) {
      const v = selectedPlan.amount != null ? selectedPlan.amount : selectedPlan.price;
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return 10;
  });

  useEffect(() => {
    let cancelled = false;

    const initPayment = async () => {
      if (!hasAuthToken()) {
        if (!cancelled) setIsInitLoading(false);
        return;
      }
      const isDonationCheckout = checkoutType === "donation";
      if (!initialClientSecret && !selectedPlan && !isDonationCheckout) {
        if (!cancelled) setIsInitLoading(false);
        return;
      }

      setIsInitLoading(true);
      setInitError("");
      try {
        if (initialClientSecret) {
          if (!cancelled) {
            setClientSecret(initialClientSecret);
            setScheduleInfo(null);
          }
          if (initialAmount != null && Number.isFinite(Number(initialAmount))) {
            if (!cancelled) setFeeAmount(Number(initialAmount));
          }
          return;
        }

        const user = getStoredUserDetail();
        const familyName =
          (user?.name && String(user.name).trim()) ||
          (user?.family_name && String(user.family_name).trim()) ||
          "Family";

        if (isDonationCheckout) {
          const amount = Number(donationAmount);
          if (!Number.isFinite(amount) || amount <= 0) {
            throw new Error("Please enter a valid donation amount.");
          }

          const donationBody = {
            amount: AMOUNT_IS_CENTS ? Math.round(amount * 100) : amount,
            family_name: familyName,
          };
          if (familyTreeIdForIntent != null && String(familyTreeIdForIntent).trim() !== "") {
            donationBody.family_tree_id = String(familyTreeIdForIntent).trim();
          }

          const donationRes = await postApi(DONATION_INTENT_PATH, donationBody);
          const donationParsed = parseLifetimeIntentResponse(donationRes);
          if (!donationParsed.clientSecret) {
            throw new Error(
              donationRes?.message ||
                "Missing client secret for donation payment. Check /subscription/donation/intent."
            );
          }
          if (!cancelled) {
            setClientSecret(donationParsed.clientSecret);
            setScheduleInfo(null);
            setFeeAmount(amount);
          }
          return;
        }

        const planId =
          selectedPlan?.id != null && String(selectedPlan.id).trim() !== ""
            ? String(selectedPlan.id).trim()
            : "";
        if (!planId) {
          throw new Error("Selected plan is missing an id. Cannot start checkout.");
        }

        if (isLifetimePlan(selectedPlan, planCategory)) {
          const lifetimeRes = await postApi(LIFETIME_INTENT_PATH, {
            family_name: familyName,
            plan_id: planId,
          });
          const lifetimeParsed = parseLifetimeIntentResponse(lifetimeRes);
          if (!lifetimeParsed.clientSecret) {
            throw new Error(
              lifetimeRes?.message ||
                "Missing client secret for lifetime payment. Check /subscription/lifetime/intent."
            );
          }
          if (!cancelled) {
            setClientSecret(lifetimeParsed.clientSecret);
            setScheduleInfo(null);
          }
          return;
        }

        const intentBody = {
          plan_id: planId,
          family_name: familyName,
        };
        if (familyTreeIdForIntent != null && String(familyTreeIdForIntent).trim() !== "") {
          intentBody.family_tree_id = String(familyTreeIdForIntent).trim();
        }
        const createRes = await postApi(SUBSCRIPTION_INTENT_PATH, intentBody);

        const parsed = parseSubscriptionIntentResponse(createRes);

        if (!cancelled && parsed.clientSecret) {
          setClientSecret(parsed.clientSecret);
          setScheduleInfo(null);
          if (parsed.amount != null && Number.isFinite(Number(parsed.amount))) {
            setFeeAmount(Number(parsed.amount));
          }
          return;
        }

        if (!cancelled && parsed.scheduleId) {
          setClientSecret("");
          setScheduleInfo({
            scheduleId: parsed.scheduleId,
            currentPeriodEnd: parsed.currentPeriodEnd,
            message: parsed.message,
            subscriptionId: parsed.subscriptionId,
          });
          if (parsed.message) {
            toast.info(parsed.message);
          }
          return;
        }

        throw new Error(
          parsed.message ||
            createRes?.message ||
            "No payment or schedule data returned. Check /subscription/intent response."
        );
      } catch (e) {
        if (!cancelled) {
          const msg =
            e?.response?.data?.message || e?.message || "Failed to start payment. Please try again later.";
          setInitError(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setIsInitLoading(false);
      }
    };

    initPayment();
    return () => {
      cancelled = true;
    };
  }, [
    initialClientSecret,
    initialAmount,
    selectedPlan,
    planCategory,
    familyTreeIdForIntent,
    checkoutType,
    donationAmount,
  ]);

  const amountCents = useMemo(() => {
    const price = Number(feeAmount);
    if (!Number.isFinite(price)) return 1000;
    return Math.round(price * 100);
  }, [feeAmount]);

  const showRenewalNotice = useMemo(() => {
    if (checkoutType === "donation" || !selectedPlan) return false;
    return !isLifetimePlan(selectedPlan, planCategory);
  }, [selectedPlan, planCategory, checkoutType]);

  const paymentSummaryName = useMemo(() => {
    const override = String(planSummaryNameOverride || "").trim();
    if (override) return override;
    if (checkoutType === "donation") return "Donation";
    return planCheckoutDisplayName(selectedPlan, planCategory);
  }, [planSummaryNameOverride, checkoutType, selectedPlan, planCategory]);

  return (
    <div className={wrapperClassName || undefined}>
      {headerSlot}

      <div className="signup-payment-inner">
        {isInitLoading && (
          <div className="signup-payment-loading-overlay" aria-live="polite">
            <div className="signup-payment-loading-text">Loading…</div>
          </div>
        )}

        {!isInitLoading && initError ? (
          <div className="signup-payment-error-banner" role="alert">
            {initError}
          </div>
        ) : null}

        {!isInitLoading && !initError && scheduleInfo && !clientSecret ? (
          <SignupScheduledPanel
            scheduleInfo={scheduleInfo}
            onContinue={onPaymentSuccess}
            isLoading={isInitLoading}
            familyTreeIdForIntent={familyTreeIdForIntent}
          />
        ) : null}

        {!isInitLoading && !initError && clientSecret && stripePromise ? (
          <Elements stripe={stripePromise}>
            <SignupPaymentForm
              amountCents={amountCents}
              clientSecret={clientSecret}
              onPaymentSuccess={onPaymentSuccess}
              planSummaryName={paymentSummaryName}
              showRenewalNotice={showRenewalNotice}
              familyTreeIdForIntent={familyTreeIdForIntent}
            />
          </Elements>
        ) : null}

        {!isInitLoading && !initError && clientSecret && !stripePromise ? (
          <div className="signup-stripe-form">
            <div className="signup-stripe-header">
              <h1 className="signup-stripe-title">Complete Payment</h1>
              <p className="signup-stripe-subtitle">Secure payment for your FamConnect26 account</p>
            </div>
            <div className="signup-payment-error-banner">
              Stripe is not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in your environment.
            </div>
          </div>
        ) : null}

        {!isInitLoading && !initError && !clientSecret && !scheduleInfo ? (
          <div className="signup-payment-error-banner" role="alert">
            Could not start checkout. Please go back and try again.
          </div>
        ) : null}
      </div>
    </div>
  );
}
